import type { components } from './generated/schema'

type DeviceWire = components['schemas']['DeviceResponse']

export type DevicePlatform = 'web' | 'ios' | 'android'

/**
 * A signed-in device as the UI sees it. There is deliberately NO `ip_address`
 * here: the parser drops it, so no component can render it.
 */
export type Device = {
  id: string
  isCurrent: boolean
  name: string
  platform: DevicePlatform | null
  place: string | null
  lastSeenAt: Date
}

function platformOf(raw: string | null): DevicePlatform | null {
  return raw === 'web' || raw === 'ios' || raw === 'android' ? raw : null
}

/** iOS `DeviceNaming.title`. */
function nameOf(wire: DeviceWire, platform: DevicePlatform | null): string {
  const trimmed = wire.device_name?.trim() ?? ''
  if (trimmed === '') return 'Unknown device'
  if (platform === 'ios') return 'iPhone'
  if (platform === 'android') return 'Android phone'
  return trimmed
}

/** The one "this device" rule: the header fingerprint, case-insensitively. */
export function isThisDevice(fingerprint: string, localId: string): boolean {
  return fingerprint.toLowerCase() === localId.toLowerCase()
}

/**
 * Wire → UI. This device is matched on `device_fingerprint` (the header value),
 * case-insensitively — never on the row `id`. The current device sorts first;
 * the rest keep the server's `last_seen_at desc` order.
 */
export function parseDevices(wire: readonly DeviceWire[], localId: string): Device[] {
  const devices = wire.map((row): Device => {
    const platform = platformOf(row.platform)
    return {
      id: row.id,
      isCurrent: isThisDevice(row.device_fingerprint, localId),
      name: nameOf(row, platform),
      platform,
      place: [row.location_city, row.location_country].filter(Boolean).join(', ') || null,
      lastSeenAt: new Date(row.last_seen_at),
    }
  })
  return [...devices.filter((d) => d.isCurrent), ...devices.filter((d) => !d.isCurrent)]
}
